import { useEffect, useRef } from 'react';
import { AgencySearchQuery } from '../../../generated/graphql';
import { notEmpty } from '../../../utils';
import {
  trackSearchResultsViewGa4,
  trackViewSearchResultsPage,
} from '../../../tracking/search-results';
import { SearchResultsState } from '../utils/get-search-from-query';
import {
  formatLocationsForGa4,
  formatSearchTerm,
  getFirstSuburbName,
} from '../utils/format-suburbs-for-tracking';
import isPropertyManagerFilter from '../utils/is-property-manager-filter';
import buildSearchResultsBaseProperties, {
  resultsCountOf,
  sortLabelOf,
} from '../utils/build-search-results-base-properties';

interface UseTrackSearchResultsView {
  data?: AgencySearchQuery;
  state: SearchResultsState | null;
  path?: string;
  loading?: boolean;
  mixpanelHasInitialized?: boolean;
}

const useTrackSearchResultsView = ({
  data,
  state,
  path,
  loading = false,
  mixpanelHasInitialized = false,
}: UseTrackSearchResultsView): void => {
  const trackedMixpanelPathRef = useRef<string | null>(null);
  const trackedGa4PathRef = useRef<string | null>(null);

  const dataRef = useRef(data);
  dataRef.current = data;
  const stateRef = useRef(state);
  stateRef.current = state;

  const locationProfilesKey =
    data?.locationProfiles
      ?.map((profile) => profile?.urlSlug)
      .filter(notEmpty)
      .join('|') ?? '';

  useEffect(() => {
    const currentData = dataRef.current;
    const currentState = stateRef.current;

    if (!path || loading || !currentData?.locationProfiles || !currentState) {
      return;
    }

    const resultsCount = resultsCountOf(currentData, currentState);
    const sortByLabel = sortLabelOf(currentState);

    if (mixpanelHasInitialized && trackedMixpanelPathRef.current !== path) {
      trackViewSearchResultsPage({
        suburb: getFirstSuburbName(currentData.locationProfiles),
        ...buildSearchResultsBaseProperties(currentData, currentState),
      });

      trackedMixpanelPathRef.current = path;
    }

    if (trackedGa4PathRef.current === path) {
      return;
    }

    const agentResults = currentData?.contactSearchBySuburbs?.results ?? [];
    const agencyResults = currentData?.agencySearchBySuburbs?.results ?? [];
    const { suburbNames, postcodes } = formatLocationsForGa4(
      currentData.locationProfiles,
    );

    trackSearchResultsViewGa4({
      suburbNames,
      postcodes,
      agentIds: currentState.isAgency
        ? []
        : agentResults.map((contact) => contact?.id).filter(notEmpty),
      agencyIds: currentState.isAgency
        ? agencyResults.map((agency) => agency?.id).filter(notEmpty)
        : [
            ...new Set(
              agentResults.map((contact) => contact?.agencyId).filter(notEmpty),
            ),
          ],
      searchTerm: formatSearchTerm(currentData.locationProfiles),
      searchType:
        !currentState.isAgency &&
        isPropertyManagerFilter(currentState.agentFilter)
          ? 'Lease'
          : 'Sell',
      searchSortBy: sortByLabel,
      searchResultCount: resultsCount,
      searchResultsDepth: currentState.page,
    });

    trackedGa4PathRef.current = path;
  }, [
    path,
    loading,
    mixpanelHasInitialized,
    locationProfilesKey,
    state?.sortBy,
    state?.agentFilter,
    state?.isAgency,
    data?.agencySearchBySuburbs?.total,
    data?.contactSearchBySuburbs?.total,
  ]);
};

export default useTrackSearchResultsView;
